import { useMutation } from '@apollo/client'
import { useCallback } from 'react'
import { graphql } from '~publish/gql'
import { useCurrentOrganization } from '~publish/contexts/AccountContext'
import type { SavedReply, SavedRepliesQueryQuery } from '~publish/gql/graphql'
import { SavedRepliesQuery } from './useSavedRepliesQuery'
import { handleSavedReplyError } from './handleSavedReplyError'

export type CreateSavedReply = (params: {
  title?: string | null
  content: string
  hotkeys?: string[]
}) => Promise<SavedReply>

export const CreateSavedReply = graphql(/* GraphQL */ `
  mutation CreateSavedReply($input: CreateSavedReplyInput!) {
    createSavedReply(input: $input) {
      ... on SavedReplyActionSuccess {
        savedReply {
          id
          organizationId
          title
          content {
            text
          }
          hotkeys
          createdAt
          updatedAt
        }
      }
      ... on MutationError {
        __typename
        message
      }
    }
  }
`)

/**
 * Hook to create a new saved reply. The new saved reply is added to the cache
 * to avoid data refetching.
 *
 * @returns {CreateSavedReply} Function to create a new saved reply.
 * @example
 * const createSavedReply = useCreateSavedReply();
 *
 * const handleCreateSavedReply = async () => {
 *   try {
 *     const newSavedReply = await createSavedReply({ title: 'New Saved Reply', content: 'This is a new saved reply' });
 *
 *   } catch (err) {
 *     console.error(err);
 *   }
 * };
 */
export function useCreateSavedReply(): CreateSavedReply {
  const organizationId = useCurrentOrganization()?.id

  const [createSavedReplyMutation] = useMutation(CreateSavedReply, {
    update(cache, { data }) {
      if (
        !data?.createSavedReply ||
        data.createSavedReply.__typename !== 'SavedReplyActionSuccess'
      )
        return

      const newItem = data.createSavedReply.savedReply

      const variables = {
        input: { organizationId },
      }

      // Read from the cache with correct variables
      const existingData = cache.readQuery<SavedRepliesQueryQuery>({
        query: SavedRepliesQuery,
        variables,
      })

      if (!existingData) return

      cache.writeQuery<SavedRepliesQueryQuery>({
        query: SavedRepliesQuery,
        variables,
        data: {
          savedReplies: {
            __typename: 'SavedRepliesResponse',
            savedReplies: [newItem, ...existingData.savedReplies.savedReplies],
          },
        },
      })
    },
  })

  const createSavedReply = useCallback(
    async ({
      title,
      content,
      hotkeys,
    }: {
      title?: string | null
      content: string
      hotkeys?: string[]
    }): Promise<SavedReply> => {
      if (!organizationId) {
        throw new Error('Organization Id is required')
      }

      const { data, errors } = await createSavedReplyMutation({
        variables: {
          input: {
            organizationId,
            savedReply: {
              title,
              content: {
                text: content,
              },
              hotkeys,
            },
          },
        },
      })

      if (errors || !data) {
        throw new Error(errors?.[0]?.message ?? 'Failed to create saved reply')
      }

      const result = data.createSavedReply

      if (result.__typename !== 'SavedReplyActionSuccess') {
        handleSavedReplyError({
          typename: result.__typename ?? '',
          operation: 'create',
          element: 'saved reply',
        })
        /* needed only for TS to be happy */
        throw new Error('Unexpected error')
      }

      return result.savedReply
    },
    [createSavedReplyMutation, organizationId],
  )

  return createSavedReply
}
